import React, { useEffect, useState } from "react";
import { fetchTowns, fetchDistance } from "../lib/api";
import { Map, ArrowRight } from "lucide-react";

export default function MileageChart() {
    const [towns, setTowns] = useState([]);
    const [from, setFrom] = useState("Aviemore");
    const [to, setTo] = useState("Grantown");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchTowns().then(d => setTowns(d.towns)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!from || !to) return;
        setLoading(true);
        fetchDistance(from, to).then(setResult).catch(() => setResult(null)).finally(() => setLoading(false));
    }, [from, to]);

    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-10 lg:py-14 fade-up" data-testid="mileage-page">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E57373] font-semibold mb-3">Tool</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: "Outfit" }}>Mileage Chart</h1>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Distances between towns across Badenoch &amp; Strathspey.
            </p>

            <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">From</label>
                        <select
                            value={from}
                            onChange={e => setFrom(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-lg focus:ring-2 focus:ring-[#2C7A85] outline-none bg-white"
                            data-testid="mileage-from"
                        >
                            {towns.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div className="hidden sm:flex pb-3 justify-center">
                        <ArrowRight className="h-5 w-5 text-gray-400" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">To</label>
                        <select
                            value={to}
                            onChange={e => setTo(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-lg focus:ring-2 focus:ring-[#2C7A85] outline-none bg-white"
                            data-testid="mileage-to"
                        >
                            {towns.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            <div className="mt-6 bg-[#2C7A85] text-white rounded-lg p-8" data-testid="mileage-result">
                <div className="flex items-start gap-4">
                    <Map className="h-6 w-6 mt-1 text-[#E57373] flex-shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm uppercase tracking-widest opacity-70 mb-1">{from} → {to}</div>
                        {loading ? (
                            <div className="text-2xl opacity-80">Calculating…</div>
                        ) : result?.miles !== null && result?.miles !== undefined ? (
                            <>
                                <div className="text-5xl sm:text-6xl font-bold" style={{ fontFamily: "Outfit" }}>{result.miles}<span className="text-2xl font-normal opacity-70"> miles</span></div>
                                <div className="text-sm mt-3 opacity-80">One-way driving distance.</div>
                            </>
                        ) : (
                            <div className="text-lg opacity-80">Distance not in the published chart — please confirm with the BSCC Office.</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-8 text-sm text-gray-500">
                Source: BSCC Driver Handbook — Mileage Chart. Some pairs were not listed in the original chart and may show as unavailable.
            </div>
        </div>
    );
}
